// /opt/isp_erp/frontend/src/components/warehouse/ImportarEquiposExcel.jsx
import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField,
  FormControl, InputLabel, Select, MenuItem, Alert, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Divider
} from '@mui/material';
import { UploadFile, Download } from '@mui/icons-material';
import { warehouseAPI } from '../../api/warehouse.api';

const detalleError = (err, porDefecto) => {
  const d = err.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join('; ');
  return porDefecto;
};

const FORM_VACIO = { proveedor_id: '', documento_compra: '', fecha_compra: '', descripcion: '' };

const ImportarEquiposExcel = () => {
  const [abierto, setAbierto] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  const [form, setForm] = useState(FORM_VACIO);
  const [archivo, setArchivo] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState('');
  const [exito, setExito] = useState(null);

  useEffect(() => {
    if (!abierto) return;
    warehouseAPI.opcionesProveedores().then((r) => setProveedores(r.data || [])).catch(() => setProveedores([]));
  }, [abierto]);

  const abrir = () => {
    setForm(FORM_VACIO);
    setArchivo(null);
    setResultado(null);
    setError('');
    setExito(null);
    setAbierto(true);
  };

  const cerrar = () => {
    if (!trabajando) setAbierto(false);
  };

  const cambiar = (campo) => (e) => {
    setForm({ ...form, [campo]: e.target.value });
    setResultado(null);
  };

  const elegirArchivo = (e) => {
    const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setArchivo(f);
    setResultado(null);
    setError('');
    e.target.value = '';
  };

  const descargarPlantilla = async () => {
    try {
      const r = await warehouseAPI.descargarPlantillaEquipos();
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_ingreso_equipos.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('No se pudo descargar la plantilla');
    }
  };

  const enviar = async (confirmar) => {
    if (!form.proveedor_id) {
      setError('Selecciona el proveedor de la compra');
      return;
    }
    if (!archivo) {
      setError('Selecciona el archivo Excel');
      return;
    }
    setTrabajando(true);
    setError('');
    try {
      const datos = new FormData();
      datos.append('archivo', archivo);
      datos.append('proveedor_id', form.proveedor_id);
      if (form.documento_compra.trim()) datos.append('documento_compra', form.documento_compra.trim());
      if (form.fecha_compra) datos.append('fecha_compra', form.fecha_compra);
      if (form.descripcion.trim()) datos.append('descripcion', form.descripcion.trim());
      datos.append('confirmar', confirmar ? 'true' : 'false');
      const r = await warehouseAPI.importarEquiposExcel(datos);
      if (confirmar) {
        setExito(r.data);
        setResultado(null);
      } else {
        setResultado(r.data);
      }
    } catch (err) {
      setError(detalleError(err, 'No se pudo procesar el archivo'));
      setResultado(null);
    } finally {
      setTrabajando(false);
    }
  };

  return (
    <>
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 3, bgcolor: '#f3f8ff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">¿Compraste varios equipos?</Typography>
          <Typography variant="body2" color="text.secondary">
            Impórtalos todos de una vez desde un Excel, indicando el proveedor y la factura.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<UploadFile />} onClick={abrir}>
          Importar desde Excel
        </Button>
      </Paper>

      <Dialog open={abierto} onClose={cerrar} maxWidth="md" fullWidth>
        <DialogTitle>Importar equipos desde Excel</DialogTitle>
        <DialogContent dividers>
          {exito ? (
            <Alert severity="success">
              {exito.message}. Lote N.º {exito.ingreso_id}. Puedes revisarlo en la pestaña "Lotes de equipos".
            </Alert>
          ) : (
            <>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              <Typography variant="subtitle2" color="primary" gutterBottom>1. Datos de la compra</Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Proveedor *</InputLabel>
                    <Select label="Proveedor *" value={form.proveedor_id} onChange={cambiar('proveedor_id')}>
                      {proveedores.map((p) => (
                        <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" label="Factura / documento de compra" value={form.documento_compra} onChange={cambiar('documento_compra')} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" type="date" label="Fecha de compra" value={form.fecha_compra} onChange={cambiar('fecha_compra')} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" label="Descripción (opcional)" value={form.descripcion} onChange={cambiar('descripcion')} />
                </Grid>
              </Grid>

              <Typography variant="subtitle2" color="primary" gutterBottom>2. Archivo</Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
                <Button variant="outlined" startIcon={<Download />} onClick={descargarPlantilla}>
                  Descargar plantilla
                </Button>
                <Button variant="outlined" component="label" startIcon={<UploadFile />}>
                  Elegir archivo .xlsx
                  <input type="file" hidden accept=".xlsx" onChange={elegirArchivo} />
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {archivo ? archivo.name : 'Ningún archivo seleccionado'}
                </Typography>
              </Box>

              {resultado && (
                <>
                  <Typography variant="subtitle2" color="primary" gutterBottom>3. Resultado de la validación</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip label={`Filas leídas: ${resultado.total_filas}`} />
                    <Chip color="success" label={`Válidas: ${resultado.validas}`} />
                    <Chip color={resultado.total_errores ? 'error' : 'default'} label={`Con errores: ${resultado.total_errores}`} />
                  </Box>
                  {resultado.puede_importar ? (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      El archivo está correcto. Se importarán {resultado.validas} equipos.
                    </Alert>
                  ) : (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Corrige las filas indicadas en el Excel, vuelve a elegir el archivo y valida otra vez. No se importa nada mientras haya errores.
                    </Alert>
                  )}
                  {resultado.errores.length > 0 && (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ bgcolor: '#f5f5f5', width: 80 }}>Fila</TableCell>
                            <TableCell sx={{ bgcolor: '#f5f5f5', width: 200 }}>SN</TableCell>
                            <TableCell sx={{ bgcolor: '#f5f5f5' }}>Problema</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {resultado.errores.map((er, i) => (
                            <TableRow key={`${er.fila}-${i}`}>
                              <TableCell>{er.fila}</TableCell>
                              <TableCell>{er.sn || '—'}</TableCell>
                              <TableCell>{er.error}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {resultado.total_errores > resultado.errores.length && (
                    <Typography variant="caption" color="text.secondary">
                      Se muestran los primeros {resultado.errores.length} errores de {resultado.total_errores}.
                    </Typography>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrar} disabled={trabajando}>{exito ? 'Cerrar' : 'Cancelar'}</Button>
          {!exito && (
            <>
              <Button
                variant="outlined"
                onClick={() => enviar(false)}
                disabled={trabajando || !archivo || !form.proveedor_id}
                startIcon={trabajando && !resultado ? <CircularProgress size={16} /> : null}
              >
                Validar archivo
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => enviar(true)}
                disabled={trabajando || !resultado || !resultado.puede_importar}
              >
                {resultado && resultado.puede_importar ? `Importar ${resultado.validas} equipos` : 'Importar'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ImportarEquiposExcel;
